import * as React from 'react';
import { Copy } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const SNIPPETS: Array<{ id: string; label: string; lang: string; code: string }> = [
  {
    id: 'node',
    label: 'Node.js',
    lang: 'javascript',
    code: `import crypto from 'node:crypto';

// WIDGET_HMAC_SECRET is the base64 secret revealed after a rotation.
const userHmac = crypto
  .createHmac('sha256', Buffer.from(WIDGET_HMAC_SECRET, 'base64'))
  .update(userId)
  .digest('hex');

// Render the widget snippet with the signed identity:
//   <script async src="…/widget.js"
//     data-api-key="pk_bot_…"
//     data-user-id="\${userId}"
//     data-user-hmac="\${userHmac}"></script>`,
  },
  {
    id: 'php',
    label: 'PHP',
    lang: 'php',
    code: `<?php
// $WIDGET_HMAC_SECRET is the base64 secret revealed after a rotation.
$userHmac = hash_hmac(
  'sha256',
  $userId,
  base64_decode($WIDGET_HMAC_SECRET)
);

// Then echo into your template:
//   <script async src="…/widget.js"
//     data-api-key="pk_bot_…"
//     data-user-id="<?= htmlspecialchars($userId) ?>"
//     data-user-hmac="<?= $userHmac ?>"></script>`,
  },
  {
    id: 'python',
    label: 'Python',
    lang: 'python',
    code: `import base64
import hashlib
import hmac

# WIDGET_HMAC_SECRET is the base64 secret revealed after a rotation.
user_hmac = hmac.new(
    base64.b64decode(WIDGET_HMAC_SECRET),
    user_id.encode("utf-8"),
    hashlib.sha256,
).hexdigest()

# Pass user_hmac + user_id to the template that renders the <script> tag.`,
  },
  {
    id: 'ruby',
    label: 'Ruby',
    lang: 'ruby',
    code: `require 'openssl'
require 'base64'

# WIDGET_HMAC_SECRET is the base64 secret revealed after a rotation.
user_hmac = OpenSSL::HMAC.hexdigest(
  'SHA256',
  Base64.decode64(WIDGET_HMAC_SECRET),
  user_id
)`,
  },
];

export function HmacBuilderCard(): JSX.Element {
  const [active, setActive] = React.useState(SNIPPETS[0]!.id);

  const copy = (code: string): void => {
    void navigator.clipboard.writeText(code);
    toast.success('Snippet copied');
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">HMAC signing — sample code</CardTitle>
        <p className="mt-1 text-xs text-muted-foreground">
          Compute the user signature on your backend with one of the snippets below.
          Send <code>userId</code> + <code>userHmac</code> through to the page that
          renders the widget. The widget never sees your secret.
        </p>
      </CardHeader>
      <CardContent>
        <Tabs value={active} onValueChange={setActive}>
          <TabsList>
            {SNIPPETS.map((s) => (
              <TabsTrigger key={s.id} value={s.id}>
                {s.label}
              </TabsTrigger>
            ))}
          </TabsList>
          {SNIPPETS.map((s) => (
            <TabsContent key={s.id} value={s.id} className="mt-3 space-y-2">
              <pre className="overflow-auto rounded-md border bg-muted/30 p-3 font-mono text-[11px] leading-relaxed">
                {s.code}
              </pre>
              <Button variant="outline" size="sm" className="gap-2" onClick={() => copy(s.code)}>
                <Copy className="h-4 w-4" />
                Copy
              </Button>
            </TabsContent>
          ))}
        </Tabs>
      </CardContent>
    </Card>
  );
}
